/**
 * df-swatches: einheitliche Varianten-Auswahl ueber Produktfamilien.
 *
 * Daten kommen aus snippets/df-swatches.liquid (JSON im Element), Geschwister-Modell:
 * - products: Geschwisterprodukte mit ihren Unterschieden (custom), ihren
 *   Shopify-Optionen und Varianten
 * - axes:   Auswahlfelder aus dem Admin (Ueberschrift + Reihenfolge)
 * - values: Swatch-Werte aus dem Admin (Achse, Name, Farbe)
 * Daraus baut buildModel() Achsen, Werte und eine Liste aller echten Varianten.
 * Zuordnung ueber die Texte der aktuellen Sprache: Optionen muessen bei allen
 * Geschwistern gleich heissen, sonst entstehen doppelte Werte.
 *
 * Regeln:
 * - Optionen zaehlen nur als Auswahlfeld, wenn mindestens ein Geschwister
 *   zwei oder mehr Werte dafuer hat. Reine Beschreibungen wie eine einzelne
 *   Option "Farbe: Schwarz Matt" werden ignoriert.
 * - Ein Wert am Produkt (custom) gilt fuer alle Varianten und hat Vorrang.
 * - Fehlt einem Geschwister eine Option, gilt der erste Wert dieser Option
 *   (z. B. Einzelpack). Fehlt ein Wert am Produkt, wird das Produkt
 *   ausgelassen (Pflegeluecke, Warnung in der Konsole).
 * - Alle Werte, die es irgendwo in der Familie gibt, werden angezeigt.
 *   Gibt es den Wert zusammen mit der aktuellen Auswahl, ist er normal klickbar,
 *   sonst ausgegraut, springt aber per Klick auf die erste passende Variante.
 * - Variante im selben Produkt: der (versteckte) Theme-Picker wird
 *   umgestellt, damit Preis, Bilder, Vorbestellung usw. wie gewohnt laufen.
 *   Anderes Produkt: normale Navigation auf ?variant=.
 */
if (!customElements.get('df-swatches')) {
  class DfSwatches extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const dataEl = this.querySelector('script[type="application/json"]');
      let raw;
      try {
        raw = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }

      const model = DfSwatches.buildModel(raw);
      this.data = { values: model.values, strings: raw.strings || {} };
      this.axes = model.axes;
      this.items = model.items;
      this.current = this.items.find((i) => i.id === raw.current) || this.items.find((i) => i.same);
      if (!this.current || !this.axes.length) return;

      // Wertereihenfolge je Achse: Reihenfolge der ersten Nennung in der Familie
      const valueOrder = Object.keys(this.data.values);
      this.axisValues = this.axes.map((axis) => valueOrder.filter(
        (key) => this.items.some((item) => item.v[axis.key] === key)
      ));

      this.productForm = this.closest('.product-form') || document;
      this.closest('.input-row')?.classList.add('df-swatches-active');

      this.addEventListener('click', this.handleClick.bind(this));
      document.addEventListener('on:variant:change', this.handleThemeVariantChange.bind(this));
      // Theme-Blasen (z. B. "Im Doppelpack 6% sparen!") werden vom Theme je Variante
      // ein-/ausgeblendet; danach den Pfeil neu ausrichten
      this.productForm.querySelector('form.js-product-form input[name="id"]')
        ?.addEventListener('change', () => setTimeout(() => this.positionFlags(), 0));
      window.addEventListener('resize', () => this.positionFlags());

      this.render();
    }

    /**
     * Rohdaten aus Liquid -> { axes, values, items }.
     * Achsen-Key = Ueberschrift, Wert-Key = Ueberschrift + Wert (aktuelle Sprache).
     */
    static buildModel(raw) {
      const norm = (s) => String(s == null ? '' : s).trim();
      const SEP = '\u0001';
      const vkey = (axis, val) => axis + SEP + val;

      const adminOrder = new Map();
      (raw.axes || []).forEach((a) => {
        const label = norm(a.label);
        if (label && !adminOrder.has(label)) adminOrder.set(label, Number(a.order) || 0);
      });
      const adminSwatch = new Map();
      (raw.values || []).forEach((w) => {
        if (w.swatch) adminSwatch.set(vkey(norm(w.axis), norm(w.label)), w.swatch);
      });

      // Optionen, die irgendwo in der Familie wirklich variieren
      const optionAxes = new Map(); // Label -> Position der Option beim ersten Auftreten
      (raw.products || []).forEach((p) => p.options.forEach((o, i) => {
        if (o.values.length >= 2 && !optionAxes.has(norm(o.name))) optionAxes.set(norm(o.name), i);
      }));

      const values = {};
      const axisFirstValue = new Map();
      const axisSeen = [];
      const addValue = (axis, val, optionSwatch) => {
        const key = vkey(axis, val);
        if (!values[key]) {
          values[key] = { label: val, swatch: adminSwatch.get(key) || optionSwatch || '' };
        } else if (!values[key].swatch && optionSwatch) {
          values[key].swatch = optionSwatch;
        }
        if (!axisFirstValue.has(axis)) {
          axisFirstValue.set(axis, key);
          axisSeen.push(axis);
        }
        return key;
      };

      const items = [];
      const customAxes = new Set();
      (raw.products || []).forEach((p) => {
        const custom = {};
        p.custom.forEach((c) => {
          const axis = norm(c.axis);
          if (!axis) return;
          customAxes.add(axis);
          custom[axis] = addValue(axis, norm(c.value));
        });
        const opts = p.options
          .map((o, i) => ({ name: norm(o.name), i, values: o.values }))
          .filter((o) => optionAxes.has(o.name) && !(o.name in custom));
        opts.forEach((o) => o.values.forEach((val) => addValue(o.name, norm(val.label), val.swatch)));

        p.variants.forEach((variant) => {
          const v = { ...custom };
          opts.forEach((o) => { v[o.name] = vkey(o.name, norm(variant.options[o.i])); });
          items.push({ id: variant.id, url: variant.url, available: variant.available, same: p.same, v });
        });
      });

      const axisLabels = axisSeen.filter((label) => optionAxes.has(label) || customAxes.has(label));
      const axes = axisLabels.map((label) => ({
        key: label,
        label,
        // Admin-Reihenfolge nur fuer Felder, die in dieser Familie auch am Produkt
        // gepflegt sind; reine Optionen behalten ihre Shopify-Reihenfolge
        order: customAxes.has(label) && adminOrder.has(label) ? adminOrder.get(label) : 100 + (optionAxes.get(label) || 0),
        type: Object.keys(values).some((k) => k.startsWith(label + SEP) && values[k].swatch) ? 'color' : 'button'
      })).sort((a, b) => a.order - b.order);

      // Fehlende Werte: Option fehlt -> erster Wert; Wert am Produkt fehlt -> Produkt auslassen
      const complete = items.filter((item) => axes.every((axis) => {
        if (item.v[axis.key]) return true;
        if (optionAxes.has(axis.key)) {
          item.v[axis.key] = axisFirstValue.get(axis.key);
          return true;
        }
        console.warn('df-swatches: Wert fehlt am Produkt', axis.key, item.id);
        return false;
      }));

      return { axes, values, items: complete };
    }

    /** Achsen-Index -> Wert der aktuellen Auswahl */
    state(index) {
      return this.current.v[this.axes[index].key];
    }

    /**
     * Beste Variante aus candidates: moeglichst viele Achsen wie bisher,
     * hoehere Achsen wiegen schwerer, verfuegbare Varianten zuerst.
     */
    pickBest(candidates) {
      const n = this.axes.length;
      let best = null;
      let bestScore = -1;
      candidates.forEach((item) => {
        let score = 0;
        this.axes.forEach((axis, j) => {
          if (item.v[axis.key] === this.state(j)) score += 2 ** (n - j);
        });
        if (item.available) score += 1;
        if (item.same) score += 0.5;
        if (score > bestScore) {
          best = item;
          bestScore = score;
        }
      });
      return best;
    }

    /**
     * Status eines Werts auf Achse i: { selected } | { target, blocked }
     * blocked = die Kombination mit der aktuellen Auswahl gibt es nicht.
     */
    resolve(i, value) {
      const key = this.axes[i].key;
      if (value === this.state(i)) return { selected: true, target: this.current };

      const withValue = this.items.filter((item) => item.v[key] === value);
      const exact = withValue.find((item) => this.axes.every(
        (axis, j) => j === i || item.v[axis.key] === this.state(j)
      ));
      if (exact) return { target: exact, blocked: false };
      return { target: this.pickBest(withValue), blocked: true };
    }

    label(value) {
      return (this.data.values[value] && this.data.values[value].label) || value;
    }

    render() {
      const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      }[c]));

      let html = '';
      this.axes.forEach((axis, i) => {
        const values = this.axisValues[i];
        // Achsen mit nur einem Wert in der ganzen Familie nicht anzeigen
        if (values.length < 2) return;

        const selectedLabel = this.label(this.state(i));
        let buttons = '';

        values.forEach((value) => {
          const res = this.resolve(i, value);
          const def = this.data.values[value] || {};
          const isColor = axis.type === 'color' && def.swatch;
          const classes = ['df-swatches__opt', isColor ? 'df-swatches__opt--color' : 'df-swatches__opt--btn'];
          if (res.selected) classes.push('is-selected');
          if (res.blocked) classes.push('is-blocked');
          if (res.target && !res.target.available) classes.push('is-soldout');

          const style = isColor
            ? ` style="--df-swatch:${def.swatch.startsWith('url(') ? 'transparent' : esc(def.swatch)};${def.swatch.startsWith('url(') ? `--df-swatch-img:${esc(def.swatch)};` : ''}"`
            : '';
          const title = res.blocked ? `${this.label(value)}: ${this.data.strings.unavailable}` : this.label(value);

          buttons += `<button type="button" role="radio" class="${classes.join(' ')}"`
            + ` aria-checked="${res.selected ? 'true' : 'false'}"`
            + `${res.target && !res.selected ? ` data-target="${res.target.id}"` : ''}`
            + ` title="${esc(title)}" aria-label="${esc(title)}"${style}>`
            + `${isColor ? '<span class="df-swatches__dot"></span>' : `<span class="df-swatches__txt">${esc(this.label(value))}</span>`}`
            + '</button>';
        });

        html += `<div class="df-swatches__axis df-swatches__axis--${esc(axis.type)}" data-axis="${esc(axis.key)}">`
          + `<div class="df-swatches__label label">${esc(axis.label)}: <span class="df-swatches__value">${esc(selectedLabel)}</span></div>`
          + `<div class="df-swatches__opts" role="radiogroup" aria-label="${esc(axis.label)}">${buttons}</div>`
          + '</div>';
      });

      let root = this.querySelector('.df-swatches__root');
      if (!root) {
        root = document.createElement('div');
        root.className = 'df-swatches__root';
        this.appendChild(root);
      }
      root.innerHTML = html;
      this.classList.add('is-ready');
      requestAnimationFrame(() => this.positionFlags());
    }

    /**
     * Pfeil der Theme-Blase (Block "metafield_flag") auf den Button richten,
     * dessen Name in der Blase vorkommt, z. B. "Doppelpack". Ohne Treffer bleibt
     * die im Theme-Editor eingestellte Position.
     */
    positionFlags() {
      const buttons = [...this.querySelectorAll('.df-swatches__opt')];
      this.productForm.querySelectorAll('.containerpreisblase:not(.hidden) .preisblase').forEach((bubble) => {
        const text = bubble.textContent.toLowerCase();
        const btn = buttons.find((b) => {
          const label = (b.getAttribute('aria-label') || '').split(':')[0].trim().toLowerCase();
          return label.length > 2 && text.includes(label);
        });
        if (!btn) {
          bubble.style.removeProperty('--df-flag-arrow');
          bubble.style.removeProperty('min-width');
          return;
        }
        const x = btn.getBoundingClientRect().left + btn.offsetWidth / 2 - bubble.getBoundingClientRect().left;
        bubble.style.setProperty('--df-flag-arrow', `${Math.round(x)}px`);
        bubble.style.minWidth = `${Math.ceil(x + 24)}px`;
      });
    }

    handleClick(evt) {
      const btn = evt.target.closest('[data-target]');
      if (!btn || !this.contains(btn)) return;
      evt.preventDefault();

      const target = this.items.find((item) => String(item.id) === btn.dataset.target);
      if (!target || target === this.current) return;
      this.goTo(target);
    }

    goTo(target) {
      if (target.same && this.selectInThemePicker(target.id)) {
        this.current = target;
        this.render();
        return;
      }
      this.classList.add('is-loading');
      window.location.href = target.url;
    }

    /**
     * Stellt den versteckten Theme-Picker auf die Variante um.
     * Liefert false, wenn das nicht geht (dann wird navigiert).
     */
    selectInThemePicker(variantId) {
      const picker = this.productForm.querySelector('variant-picker');
      if (!picker || !picker.data || !picker.data.variants) return false;
      const variant = picker.data.variants.find((v) => v.id === variantId);
      if (!variant) return false;

      const selectors = picker.querySelectorAll('.option-selector:not(.option-selector--custom)');
      if (selectors.length !== variant.options.length) return false;

      let lastInput = null;
      for (let k = 0; k < selectors.length; k += 1) {
        const inputs = [...selectors[k].querySelectorAll('input.js-option')];
        const input = inputs.find((el) => el.value === variant.options[k]);
        if (!input) return false;
        input.checked = true;
        lastInput = input;
      }
      lastInput.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    /** Theme-Picker hat gewechselt (z. B. ueber ?variant= beim Laden) */
    handleThemeVariantChange(evt) {
      if (!evt.detail || !evt.detail.variant) return;
      if (evt.detail.form && !this.productForm.contains(evt.detail.form)) return;
      const item = this.items.find((i) => i.id === evt.detail.variant.id);
      if (item && item !== this.current) {
        this.current = item;
        this.render();
      }
    }
  }

  customElements.define('df-swatches', DfSwatches);
}
